import { Card, Colors, Icon, Tabs } from '@amelio/ui-library';

import { ManagerDeviationView } from './ManagerDeviationView';
import { ManagerDifferentiationView } from './ManagerDifferentiationView';
import { MANAGER_PIVOTS, type ManagerPivot } from '../utils/calibrationViews';

import type { CalibrationReport, CalibrationView } from '@/types/calibration';
import type { IconProps, TabItem } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';

const PIVOT_ICON_NAME: Record<ManagerPivot, IconProps['name']> = {
    ratingLevel: 'essential-chart',
    differentiation: 'business-chart-1',
};

export interface ManagerViewCardProps {
    report: CalibrationReport;
    activeView: CalibrationView;
    onViewChange: (view: CalibrationView) => void;
    language: string;
}

/**
 * The manager tile's card: a full-width tab strip switching between the two pivots, then the
 * pivot itself. `variant='underline'` rather than `segmented` — the design marks the active pivot
 * with a primary underline running along the card's own top border, which is exactly what the
 * underline variant draws; the segmented variant is an inline pill group with no underline.
 */
export const ManagerViewCard = ({
    report,
    activeView,
    onViewChange,
    language,
}: ManagerViewCardProps) => {
    const { t } = useAmelioTranslations();

    const pivotItems: TabItem[] = MANAGER_PIVOTS.map((pivot) => ({
        key: pivot,
        dataTestId: `calibration-pivot-${pivot}`,
        label: (
            <span className={'inline-flex items-center gap-2'}>
                <Icon
                    name={PIVOT_ICON_NAME[pivot]}
                    size={'sm'}
                    color={pivot === activeView ? Colors.PRIMARY : Colors.BASE_400}
                    ariaHidden
                />

                {t(`calibration.pivots.${pivot}`)}
            </span>
        ),
    }));

    return (
        <Card
            glass={'sm'}
            borderRadius={'2xl'}
            shadow={'sm'}
            padding={'none'}
            dataTestId={'calibration-manager-card'}
        >
            <Tabs
                items={pivotItems}
                activeKey={activeView}
                onChange={(key) => onViewChange(key as CalibrationView)}
                variant={'underline'}
                className={'w-full'}
                dataTestId={'calibration-pivot-tabs'}
            />

            {activeView === 'differentiation' ? (
                <ManagerDifferentiationView
                    report={report}
                    language={language}
                />
            ) : (
                <ManagerDeviationView
                    report={report}
                    language={language}
                />
            )}
        </Card>
    );
};
